import { type FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import CustomPdfExportModal from 'src/components/custom-pdf-export-modal/CustomPdfExportModal';

import type { FilterModal } from '@/components/customisable-ribbon/customisableRibbonModalSchema';
import { useExportOptions } from '@/hooks/useExportOptions';

import type { useGetCollectionTableProps } from './config';
import type { AssessmentResultRegisterFields } from './types';

interface Props {
  title: string;
  /** Already-formatted count display string (e.g. `(12)`) — rendered verbatim. */
  counter?: string;
  tableProps: ReturnType<typeof useGetCollectionTableProps>;
  getActiveRibbonFilters: () => FilterModal[];
  /** True when the user may delete any assessment-result type. */
  hasDeletePermission: boolean;
  /** True when nothing is selected — greys the Delete action. */
  isDeleteDisabled: boolean;
  /** Opens the bulk-delete confirmation modal (owned by the page). */
  onDelete: () => void;
}

/**
 * Object toolbar for the Assessment Results (findings) register, mounted only
 * when the `object_toolbar` flag is on. This register has no create action but a
 * selection-driven bulk delete, which the shared register builder does not
 * model — so the config is assembled inline here (export "Download" group,
 * relocated ribbon "Edit Filters", and a `danger` bulk-delete gated by the
 * page's permission booleans + selection state). With the flag off this
 * component never renders, so `useExportOptions` runs on exactly one path
 * (toolbar vs the legacy ExportButton).
 */
export const AssessmentResultsRegisterToolbar: FC<Props> = ({
  title,
  counter,
  tableProps,
  getActiveRibbonFilters,
  hasDeletePermission,
  isDeleteDisabled,
  onDelete,
}) => {
  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'assessmentResults',
  });
  const [showPdfModal, setShowPdfModal] = useState(false);
  const ribbonConfig = useObjectToolbarFormStore((s) => s.ribbonConfigState);
  const onShowCustomPdfModal = useCallback(() => setShowPdfModal(true), []);

  const { exportOptions, exportToPdf } =
    useExportOptions<AssessmentResultRegisterFields>({
      tableProps,
      entityLabel: title,
      getActiveRibbonFilters,
      onShowCustomPdfModal,
    });

  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: { title, counter },
      actions: {
        ...(ribbonConfig
          ? {
              overflow: [
                {
                  id: 'edit',
                  label: t('edit'),
                  actions: [
                    {
                      id: 'edit-ribbon',
                      label: t('objectToolbar.filterSettings'),
                      iconName: 'filter-lines',
                      onClick: ribbonConfig.openEditRibbon,
                    },
                  ],
                },
              ],
            }
          : {}),
        export: [
          {
            id: 'export',
            actions: exportOptions.map((option) => ({
              id: option.id,
              label: option.text,
              iconName: 'download-01' as const,
              onClick: option.exportFunction,
              disabled: option.disabled,
            })),
          },
        ],
        ...(hasDeletePermission
          ? {
              danger: [
                {
                  id: 'delete',
                  label: st('delete_button'),
                  iconName: 'trash-01',
                  disabled: isDeleteDisabled,
                  onClick: onDelete,
                },
              ],
            }
          : {}),
      },
    }),
    [
      title,
      counter,
      exportOptions,
      ribbonConfig,
      hasDeletePermission,
      isDeleteDisabled,
      onDelete,
      t,
      st,
    ]
  );

  return (
    <>
      <ObjectToolbar config={toolbarConfig} />
      <CustomPdfExportModal
        exportToPdf={async (options) => {
          await exportToPdf({
            titleOverride: options.title,
            subtitleOverride: options.subtitle,
            hideRibbon: options.hideRibbon,
            orientation: options.orientation,
          });
        }}
        entityLabel={title}
        showModal={showPdfModal}
        setShowModal={setShowPdfModal}
        showRibbonOptions={true}
      />
    </>
  );
};
